/** The band's confirmations: open, release the surprise, continue the year, release results. Each closes itself when the call succeeds. */
import { useState, type ReactNode } from "react";
import type { BoardTeamRow, RoundSummary } from "@bsim/contracts";
import { Dialog, DialogCancel } from "../theme/Dialog";
import { GxNotice } from "../theme/primitives";
import type { Act } from "./useAct";

interface Ctx { readonly cohortId: string; readonly round: RoundSummary; readonly act: Act; readonly onClose: () => void }

export function Confirm({ title, lead, confirmLabel, disabled = false, onConfirm, onClose, children, testId }: {
  readonly title: string; readonly lead?: ReactNode; readonly confirmLabel: string; readonly disabled?: boolean; readonly onConfirm: () => Promise<boolean>; readonly onClose: () => void; readonly children?: ReactNode; readonly testId: string;
}) {
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); const ok = await onConfirm(); setBusy(false); if (ok) onClose(); };
  return (
    <Dialog title={title} lead={lead} testId={testId} onClose={onClose}
      footer={<><span className="gx-grow" /><DialogCancel onClose={onClose} /><button type="button" className="gx-btn gx-btn--primary" disabled={disabled || busy} data-testid={`${testId}.confirm`} onClick={() => void go()}>{busy ? <><span className="gx-spinner" aria-hidden="true" data-testid={`${testId}.spin`} />Working…</> : confirmLabel}</button></>}>
      {children === undefined ? null : <div className="gx-fac-dbody">{children}</div>}
      {busy ? <p className="gx-small gx-muted" role="status" data-testid={`${testId}.busy`}>Working on it. This dialog closes when the board has the new state.</p> : null}
    </Dialog>
  );
}

/** Minutes field: blank means "no countdown" (or the default when the caller says so). */
function Minutes({ label, value, onChange, max }: { readonly label: string; readonly value: string; readonly onChange: (v: string) => void; readonly max: number }) {
  return (
    <label className="gx-fac-field">
      <span>{label}</span>
      <input className="gx-fac-input gx-num" type="number" min={1} max={max} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

const parseMinutes = (v: string): number | null => (v.trim() === "" ? null : Number(v));

export function OpenDialog({ cohortId, round, act, onClose, defaultMinutes, teams }: Ctx & { readonly defaultMinutes: number | null; readonly teams: readonly BoardTeamRow[] }) {
  const [minutes, setMinutes] = useState(defaultMinutes === null ? "" : String(defaultMinutes));
  const incomplete = teams.filter((t) => t.missingRoles.length > 0);
  // Round 1 also waits for every human team's strategy statement (the same guard the queue lists); the reason is shown here, before Confirm, not after a refusal.
  const noStrategy = round.roundNo === 1 ? teams.filter((t) => !t.isAi && !t.strategySet) : [];
  return (
    <Confirm title={`Open Round ${round.roundNo}?`} testId="dialog.open" confirmLabel="Open round" disabled={incomplete.length > 0 || noStrategy.length > 0} onClose={onClose}
      lead="Teams can start. The countdown is display only: nothing happens at zero."
      // The refusal is already told in a toast, so the dialog closes either way and the board shows what is still missing.
      onConfirm={async () => { await act("roundOpen", { cohortId, roundId: round.id }, { expectedStateVersion: round.stateVersion, countdownMinutes: parseMinutes(minutes) }); return true; }}>
      {noStrategy.length > 0 ? (
        <GxNotice>
          <span data-testid="open.noStrategy">Round 1 opens once every team's CEO has written and saved the strategy statement. Still waiting on: {noStrategy.map((t) => t.name).join(", ")}.</span>
        </GxNotice>
      ) : null}
      {incomplete.length > 0 ? (
        <GxNotice>
          <span data-testid="open.blocked">Every team needs a CEO and a risk officer before the round can open: {incomplete.map((t) => `${t.name} (${t.missingRoles.map((r) => (r === "cro" ? "risk officer" : r.toUpperCase())).join(", ")})`).join("; ")}. Fix it in Roster.</span>
        </GxNotice>
      ) : null}
      <Minutes label="Countdown minutes (blank for none)" value={minutes} onChange={setMinutes} max={240} />
    </Confirm>
  );
}

export function ReleaseSurpriseDialog({ cohortId, round, act, onClose, defaultMinutes }: Ctx & { readonly defaultMinutes: number | null }) {
  const [minutes, setMinutes] = useState(defaultMinutes === null ? "" : String(defaultMinutes));
  return (
    <Confirm title="Release the surprise?" testId="dialog.surprise" confirmLabel="Release surprise" onClose={onClose}
      lead="Affected teams see the card now, with the numbers as of the month the year stopped."
      onConfirm={async () => (await act("surpriseRelease", { cohortId, roundId: round.id }, { expectedStateVersion: round.stateVersion, countdownMinutes: parseMinutes(minutes) })).ok}>
      <Minutes label="Response countdown minutes (blank for none)" value={minutes} onChange={setMinutes} max={30} />
    </Confirm>
  );
}

export function ContinueDialog({ cohortId, round, act, onClose, unanswered }: Ctx & { readonly unanswered: readonly BoardTeamRow[] }) {
  return (
    <Confirm title="Continue the year?" testId="dialog.continue" confirmLabel={unanswered.length > 0 ? "Continue anyway" : "Continue the year"} onClose={onClose}
      lead="The engine finishes the year. A team that has not answered takes the card's default."
      onConfirm={async () => (await act("surpriseContinue", { cohortId, roundId: round.id }, { expectedStateVersion: round.stateVersion, force: unanswered.length > 0 }, { idempotent: true })).ok}>
      {unanswered.length > 0 ? <GxNotice><span data-testid="continue.unanswered">Not answered yet: {unanswered.map((t) => t.name).join(", ")}. They take the default.</span></GxNotice> : null}
    </Confirm>
  );
}

/**
 * RELEASE waits while the round's narratives are still being written (machine guard `narrativesGeneratedOrWaived`); a refusal on
 * that guard offers the waiver. Held narratives do not block release: they publish when the facilitator approves them.
 */
export function ReleaseResultsDialog({ cohortId, round, act, onClose, heldNarratives }: Ctx & { readonly heldNarratives: number }) {
  const [writing, setWriting] = useState(false);
  const [waive, setWaive] = useState(false);
  return (
    <Confirm title={`Release Round ${round.roundNo} results?`} testId="dialog.release" confirmLabel="Release results" disabled={writing && !waive} onClose={onClose}
      lead="Results and the public market go to every team."
      onConfirm={async () => {
        const r = await act("roundRelease", { cohortId, roundId: round.id }, { expectedStateVersion: round.stateVersion, waiveNarrativeWait: waive });
        if (!r.ok && JSON.stringify(r.error.details ?? {}).includes("narrativesGeneratedOrWaived")) setWriting(true);
        return r.ok;
      }}>
      {heldNarratives > 0 ? <p className="gx-small gx-muted">{heldNarratives} held {heldNarratives === 1 ? "narrative waits" : "narratives wait"} for your review; {heldNarratives === 1 ? "it publishes" : "they publish"} when you approve.</p> : null}
      {writing ? (
        <label className="gx-fac-chk" data-testid="release.waive">
          <input type="checkbox" checked={waive} onChange={(e) => setWaive(e.target.checked)} />
          The round's narratives are still being written. Release without waiting.
        </label>
      ) : null}
    </Confirm>
  );
}
